import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  TextField,
  Typography,
} from '@mui/material';
import SEO from '../components/SEO';
import { auth } from '../firebase';
import {
  applyActionCode,
  confirmPasswordReset,
  verifyPasswordResetCode,
} from 'firebase/auth';
import logger from '../services/logger';

const PASSWORD_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;

function AuthAction() {
  const location = useLocation();
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading');
  const [error, setError] = useState('');
  const [email, setEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPasswordValue, setConfirmPasswordValue] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [actionMeta, setActionMeta] = useState({ mode: null, oobCode: null });
  const handled = useRef(false); // avoid double-processing in React 18 StrictMode

  useEffect(() => {
    if (handled.current) return;
    const params = new URLSearchParams(location.search);
    const mode = params.get('mode');
    const oobCode = params.get('oobCode');

    if (!mode || !oobCode) {
      setError('Invalid or missing action code.');
      setStatus('error');
      return;
    }

    // Remove all query parameters from the URL immediately after reading them,
    // so refreshes and SPA navigations never expose tokens in the address bar.
    window.history.replaceState(null, '', location.pathname);

    handled.current = true;
    setActionMeta({ mode, oobCode });

    if (mode === 'verifyEmail') {
      applyActionCode(auth, oobCode)
        .then(async () => {
          try {
            if (auth.currentUser) {
              await auth.currentUser.reload();
            }
          } catch (reloadError) {
            logger.error('Failed to reload user after verification', reloadError);
          }
          setStatus('verified');
        })
        .catch((err) => {
          logger.error('Email verification link invalid or expired', err);
          setError('Unable to verify email. The link may have expired or was already used.');
          setStatus('error');
        });
    } else if (mode === 'resetPassword') {
      verifyPasswordResetCode(auth, oobCode)
        .then((retrievedEmail) => {
          setEmail(retrievedEmail);
          setStatus('resetReady');
        })
        .catch((err) => {
          logger.error('Password reset link invalid or expired', err);
          setError('Unable to reset password. The link may have expired or was already used.');
          setStatus('error');
        });
    } else {
      setError('Unsupported authentication action.');
      setStatus('error');
    }
  }, [location.search]);

  const handlePasswordResetSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!PASSWORD_REGEX.test(newPassword)) {
      setError('Password must be at least 8 characters long and include uppercase, lowercase, number, and special character.');
      return;
    }
    if (newPassword !== confirmPasswordValue) {
      setError('Passwords do not match.');
      return;
    }

    setSubmitting(true);
    try {
      await confirmPasswordReset(auth, actionMeta.oobCode, newPassword);
      setStatus('resetComplete');
    } catch (err) {
      logger.error('Failed to confirm password reset', err);
      setError('Unable to update password. Please request a new reset email.');
    } finally {
      setSubmitting(false);
    }
  };

  const renderContent = () => {
    if (status === 'loading') {
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 6 }}>
          <CircularProgress />
          <Typography variant="h6" sx={{ mt: 2 }}>
            Verifying request...
          </Typography>
        </Box>
      );
    }

    if (status === 'verified') {
      return (
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
            Email verified successfully!
          </Typography>
          <Typography sx={{ mb: 3, color: '#4b5563' }}>
            Thanks for confirming your email. You can now sign in.
          </Typography>
          <Button variant="contained" onClick={() => navigate('/login')}>
            Go to Login
          </Button>
        </Box>
      );
    }

    if (status === 'resetReady') {
      return (
        <Box component="form" onSubmit={handlePasswordResetSubmit}>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
            Reset your password
          </Typography>
          <Typography sx={{ mb: 3, color: '#4b5563' }}>
            Updating password for <strong>{email}</strong>
          </Typography>
          <TextField
            fullWidth
            type="password"
            label="New password"
            sx={{ mb: 2 }}
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            required
          />
          <TextField
            fullWidth
            type="password"
            label="Confirm new password"
            sx={{ mb: 2 }}
            value={confirmPasswordValue}
            onChange={(e) => setConfirmPasswordValue(e.target.value)}
            required
          />
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <Button
            type="submit"
            variant="contained"
            fullWidth
            disabled={submitting}
          >
            {submitting ? 'Updating...' : 'Update password'}
          </Button>
        </Box>
      );
    }

    if (status === 'resetComplete') {
      return (
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
            Password updated!
          </Typography>
          <Typography sx={{ mb: 3, color: '#4b5563' }}>
            You can now sign in with your new password.
          </Typography>
          <Button variant="contained" onClick={() => navigate('/login')}>
            Return to Login
          </Button>
        </Box>
      );
    }

    return (
      <Box sx={{ textAlign: 'center' }}>
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}
        <Button variant="contained" onClick={() => navigate('/login')}>
          Back to Login
        </Button>
      </Box>
    );
  };

  return (
    <>
      <SEO
        title="Account Action - Sniffr.ai"
        description="Manage verification and password reset actions for your Sniffr.ai account."
        url="/auth/action"
      />
      <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5', py: 6 }}>
        <Container maxWidth="sm">
          <Card sx={{ borderRadius: 3, boxShadow: '0 20px 40px rgba(15, 23, 42, 0.1)' }}>
            <CardContent>
              {renderContent()}
            </CardContent>
          </Card>
        </Container>
      </Box>
    </>
  );
}

export default AuthAction;
